/* Sakal motion — GSAP 3.13 + ScrollTrigger + SplitText (vendored in /assets, standard GSAP licence).
 * Opt-in hooks (all content is fully visible without this file):
 *   [data-sk-reveal]        section heads / blocks fade up once when they enter (never if already on screen)
 *   [data-sk-stagger]       children stagger in (grids, lists)
 *   [data-sk-split]         short headline (< ~10 words) word reveal; SplitText reverted on cleanup
 *   [data-sk-stack]         layered book covers (immersive hero): entrance + gentle pointer parallax (fine pointers only)
 *   [data-sk-lines]         manifesto lines reveal in sequence
 *   [data-sk-timeline]      vertical timeline rule draws as it scrolls (scrub, no pinning)
 * One gsap.context per Shopify section; reverted on shopify:section:unload → no duplicate triggers after editor reloads.
 * gsap.matchMedia: reduced-motion users get the final state; no scroll hijacking; no pinning.
 */
(() => {
  if (window.SakalMotion || !window.gsap) return;
  const { gsap } = window;
  if (window.ScrollTrigger) gsap.registerPlugin(window.ScrollTrigger);
  if (window.SplitText) gsap.registerPlugin(window.SplitText);
  const contexts = new Map(); // section element -> gsap.matchMedia instance
  const inView = (el) => { const r = el.getBoundingClientRect(); return r.top < window.innerHeight && r.bottom > 0; };

  function build(section) {
    const mm = gsap.matchMedia(section);
    mm.add(
      { motion: '(prefers-reduced-motion: no-preference)', reduced: '(prefers-reduced-motion: reduce)', fine: '(hover: hover) and (pointer: fine)' },
      (ctx) => {
        const { motion, fine } = ctx.conditions;
        if (!motion) return; // reduced motion: leave everything in its final, readable state
        const q = gsap.utils.selector(section);
        const cleanups = [];

        q('[data-sk-reveal]').forEach((el) => {
          if (inView(el)) return; // never hide what the user can already see (protects LCP)
          gsap.from(el, { autoAlpha: 0, y: 14, duration: 0.5, ease: 'power2.out', scrollTrigger: { trigger: el, start: 'top 90%', once: true } });
        });

        q('[data-sk-stagger]').forEach((list) => {
          const items = Array.from(list.children);
          if (!items.length || inView(list)) return;
          gsap.from(items, { autoAlpha: 0, y: 16, duration: 0.45, ease: 'power2.out', stagger: { each: 0.05, grid: 'auto' }, scrollTrigger: { trigger: list, start: 'top 88%', once: true } });
        });

        q('[data-sk-split]').forEach((el) => {
          if (!window.SplitText || el.textContent.trim().split(/\s+/).length > 12) return;
          const split = window.SplitText.create(el, { type: 'words', mask: 'words', aria: 'auto' });
          gsap.from(split.words, { yPercent: 105, duration: 0.7, ease: 'expo.out', stagger: 0.045, delay: 0.05 });
          cleanups.push(() => split.revert());
        });

        q('[data-sk-stack]').forEach((stack) => {
          const covers = stack.querySelectorAll('[data-sk-stack-item]');
          gsap.from(covers, { autoAlpha: 0, y: 40, rotate: (i) => (i % 2 ? 6 : -6), duration: 0.9, ease: 'expo.out', stagger: 0.08 });
          if (!fine) return;
          const xTo = Array.from(covers).map((c) => gsap.quickTo(c, 'x', { duration: 0.8, ease: 'power3.out' }));
          const yTo = Array.from(covers).map((c) => gsap.quickTo(c, 'y', { duration: 0.8, ease: 'power3.out' }));
          const move = (e) => {
            const r = stack.getBoundingClientRect();
            const dx = (e.clientX - r.left) / r.width - 0.5, dy = (e.clientY - r.top) / r.height - 0.5;
            covers.forEach((c, i) => { const depth = (i + 1) * 6; xTo[i](dx * depth); yTo[i](dy * depth); });
          };
          stack.addEventListener('pointermove', move);
          cleanups.push(() => stack.removeEventListener('pointermove', move));
        });

        q('[data-sk-lines]').forEach((block) => {
          const lines = block.querySelectorAll('[data-sk-line-item]');
          if (inView(block)) return;
          gsap.from(lines, { autoAlpha: 0, x: -18, duration: 0.6, ease: 'power2.out', stagger: 0.12, scrollTrigger: { trigger: block, start: 'top 80%', once: true } });
        });

        q('[data-sk-timeline]').forEach((tl) => {
          const rule = tl.querySelector('[data-sk-timeline-rule]');
          if (!rule || !window.ScrollTrigger) return;
          gsap.fromTo(rule, { scaleY: 0 }, { scaleY: 1, ease: 'none', transformOrigin: 'top center', scrollTrigger: { trigger: tl, start: 'top 75%', end: 'bottom 60%', scrub: 0.6 } });
        });
        return () => cleanups.forEach((fn) => fn());
      }
    );
    return mm;
  }

  function init(scope) {
    const root = scope || document;
    const sections = root.matches && root.matches('.shopify-section') ? [root] : Array.from(root.querySelectorAll('.shopify-section'));
    sections.forEach((s) => {
      if (contexts.has(s)) return;
      if (!s.querySelector('[data-sk-reveal],[data-sk-stagger],[data-sk-split],[data-sk-stack],[data-sk-lines],[data-sk-timeline]')) return;
      contexts.set(s, build(s));
    });
    if (window.ScrollTrigger) window.ScrollTrigger.refresh();
  }
  function destroy(section) {
    const mm = contexts.get(section);
    if (mm) { mm.revert(); contexts.delete(section); }
  }

  document.addEventListener('shopify:section:unload', (e) => destroy(e.target));
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  // Late content (lazy tabs, recommendations) asks for a refresh so trigger positions stay correct.
  document.addEventListener('sakal:content:changed', () => window.ScrollTrigger && window.ScrollTrigger.refresh());

  window.SakalMotion = { init, destroy };
  const start = () => (window.requestIdleCallback ? requestIdleCallback(() => init(document), { timeout: 1200 }) : setTimeout(() => init(document), 150));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
